import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import SEO, { generateVehicleSchema, generateBreadcrumbSchema } from "../components/SEO";
import { Button } from "../components/ui/button";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  Truck, Fuel, Ruler, Package, Users, Calendar as CalendarIcon, 
  Settings, ArrowLeft, MapPin, CheckCircle2, Loader2, ChevronLeft, ChevronRight, Clock, Gauge, CreditCard
} from "lucide-react";
import axios from "axios";
import { format, differenceInDays } from "date-fns";
import { it } from "date-fns/locale";
import { formatCurrency, cn } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function VehicleDetailPage() {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, token } = useAuth();
  
  const [vehicle, setVehicle] = useState(null);
  const [company, setCompany] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Booking state
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [pickupTime, setPickupTime] = useState("");
  const [returnTime, setReturnTime] = useState("");
  const [kmTier, setKmTier] = useState("100");
  const [notes, setNotes] = useState("");
  const [bookingLoading, setBookingLoading] = useState(false);

  // KM tiers
  const KM_TIERS = [
    { id: "100", label: "100 km/giorno" },
    { id: "200", label: "200 km/giorno" },
    { id: "300", label: "300 km/giorno" },
    { id: "400", label: "400 km/giorno" },
    { id: "500", label: "500 km/giorno" },
    { id: "illimitati", label: "Km Illimitati" },
  ];

  // Generate time options
  const timeOptions = [];
  for (let h = 7; h <= 20; h++) {
    timeOptions.push(`${h.toString().padStart(2, '0')}:00`);
    timeOptions.push(`${h.toString().padStart(2, '0')}:30`);
  }

  useEffect(() => {
    fetchVehicle();
  }, [vehicleId]);

  const fetchVehicle = async () => {
    try {
      const vehicleRes = await axios.get(`${API}/vehicles/${vehicleId}`);
      setVehicle(vehicleRes.data);
      
      const companyRes = await axios.get(`${API}/companies/${vehicleRes.data.company_id}`);
      setCompany(companyRes.data);
      
      // Fetch pricing for this vehicle type
      const pricingRes = await axios.get(`${API}/companies/${vehicleRes.data.company_id}/pricing/${vehicleRes.data.vehicle_type || 'medio'}`);
      setPricing(pricingRes.data);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Veicolo non trovato");
      navigate("/catalogo");
    } finally {
      setLoading(false);
    }
  };

  // Get price for selected km tier from pricing
  const getPriceForTier = (tierId) => {
    if (!pricing || !pricing.tiers) return null;
    const tier = pricing.tiers.find(t => t.km_tier === tierId);
    return tier ? tier.price : null;
  };

  const calculatePrice = () => {
    if (!startDate || !endDate || !vehicle) return 0;
    const days = differenceInDays(endDate, startDate) || 1;
    
    // Try to get price from company pricing
    const tierPrice = getPriceForTier(kmTier);
    if (tierPrice) {
      return days * tierPrice;
    }
    
    // Fallback to vehicle daily rate
    return days * vehicle.daily_rate;
  };

  const handleBooking = async () => {
    if (!isAuthenticated) {
      toast.error("Devi accedere per prenotare");
      navigate("/login");
      return;
    }

    if (!startDate || !endDate) {
      toast.error("Seleziona le date di noleggio");
      return;
    }

    if (!pickupTime || !returnTime) {
      toast.error("Seleziona l'ora di ritiro e consegna");
      return;
    }

    setBookingLoading(true);
    try {
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.post(`${API}/bookings`, {
        vehicle_id: vehicleId,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        pickup_time: pickupTime,
        km_tier: kmTier,
        return_time: returnTime,
        notes: notes || null
      }, { headers, withCredentials: true });

      toast.success("Prenotazione inviata con successo!");
      setShowBookingDialog(false);
      // Reset form
      setStartDate(null);
      setEndDate(null);
      setPickupTime("");
      setReturnTime("");
      setNotes("");
      navigate("/prenotazioni");
    } catch (error) {
      console.error("Booking error:", error);
      toast.error(error.response?.data?.detail || "Errore durante la prenotazione");
    } finally {
      setBookingLoading(false);
    }
  };

  const getImageUrl = (img) => {
    if (!img) return "https://images.unsplash.com/photo-1646363976310-ca9509f17260?crop=entropy&cs=srgb&fm=jpg&q=85";
    if (img.startsWith('http')) return img;
    return `${process.env.REACT_APP_BACKEND_URL}${img}`;
  };

  const images = vehicle?.images?.length > 0 
    ? vehicle.images 
    : ["https://images.unsplash.com/photo-1646363976310-ca9509f17260?crop=entropy&cs=srgb&fm=jpg&q=85"];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!vehicle) return null;

  const vehicleSchema = generateVehicleSchema(vehicle, company);
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Catalogo", url: "/catalogo" },
    { name: `${vehicle.brand} ${vehicle.model}` }
  ]);

  return (
    <div className="min-h-screen flex flex-col bg-muted/30">
      <SEO
        title={`${vehicle.brand} ${vehicle.model} - Noleggio a ${company?.city || 'Italia'}`}
        description={`Noleggia ${vehicle.brand} ${vehicle.model} a ${company?.city || 'Italia'}. ${vehicle.cargo_volume_m3}m³ di carico, ${vehicle.engine_type}. A partire da €${vehicle.daily_rate}/giorno. Prenota online!`}
        keywords={`noleggio ${vehicle.brand}, noleggio ${vehicle.model}, furgone ${company?.city}, noleggio furgoni ${company?.province}`}
        url={`/veicolo/${vehicle.vehicle_id}`}
        type="product"
        schema={[vehicleSchema, breadcrumbSchema]}
      />
      <Header />

      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/catalogo")}
          className="mb-6"
          data-testid="back-to-catalog"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Torna al catalogo
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images and Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <div className="bg-background border border-border overflow-hidden">
              <div className="aspect-video relative">
                <img
                  src={getImageUrl(images[currentImageIndex])}
                  alt={`${vehicle.brand} ${vehicle.model}`}
                  className="w-full h-full object-cover"
                />
                {images.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentImageIndex(i => i === 0 ? images.length - 1 : i - 1)}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-background/80 p-2 hover:bg-background transition-colors"
                    >
                      <ChevronLeft className="h-6 w-6" />
                    </button>
                    <button
                      onClick={() => setCurrentImageIndex(i => i === images.length - 1 ? 0 : i + 1)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-background/80 p-2 hover:bg-background transition-colors"
                    >
                      <ChevronRight className="h-6 w-6" />
                    </button>
                  </>
                )}
              </div>
              {images.length > 1 && (
                <div className="flex gap-2 p-4">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={cn(
                        "w-20 h-14 overflow-hidden border-2 transition-colors",
                        index === currentImageIndex ? "border-primary" : "border-transparent"
                      )}
                    >
                      <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Vehicle Info */}
            <div className="bg-background border border-border p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <Badge className="mb-2">{vehicle.engine_type}</Badge>
                  <h1 
                    className="text-2xl md:text-3xl font-bold tracking-tight"
                    style={{ fontFamily: 'Chivo, sans-serif' }}
                  >
                    {vehicle.brand} {vehicle.model}
                  </h1>
                  {company && (
                    <p className="text-muted-foreground flex items-center gap-1 mt-1">
                      <MapPin className="h-4 w-4" />
                      {company.name} - {company.city}, {company.province}
                    </p>
                  )}
                </div>
                <p className="text-sm font-mono text-muted-foreground">{vehicle.license_plate}</p>
              </div>

              {vehicle.description && (
                <p className="text-muted-foreground mb-6">{vehicle.description}</p>
              )}

              {/* Specifications */}
              <h3 className="font-bold uppercase tracking-wider text-sm mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Specifiche Tecniche
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-muted/50 p-4">
                  <Ruler className="h-5 w-5 text-primary mb-2" />
                  <p className="text-xs text-muted-foreground uppercase">Dimensioni</p>
                  <p className="font-mono font-bold">{vehicle.length_m}m x {vehicle.width_m}m x {vehicle.height_m}m</p>
                </div>
                <div className="bg-muted/50 p-4">
                  <Package className="h-5 w-5 text-primary mb-2" />
                  <p className="text-xs text-muted-foreground uppercase">Volume Carico</p>
                  <p className="font-mono font-bold">{vehicle.cargo_volume_m3} m³</p>
                </div>
                <div className="bg-muted/50 p-4">
                  <Truck className="h-5 w-5 text-primary mb-2" />
                  <p className="text-xs text-muted-foreground uppercase">Portata Max</p>
                  <p className="font-mono font-bold">{vehicle.max_payload_kg} kg</p>
                </div>
                <div className="bg-muted/50 p-4">
                  <Users className="h-5 w-5 text-primary mb-2" />
                  <p className="text-xs text-muted-foreground uppercase">Posti</p>
                  <p className="font-mono font-bold">{vehicle.seats}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <div className="flex items-center gap-2">
                  <Fuel className="h-5 w-5 text-muted-foreground" />
                  <span>{vehicle.engine_type}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Settings className="h-5 w-5 text-muted-foreground" />
                  <span>{vehicle.transmission}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5 text-muted-foreground" />
                  <span>Anno {vehicle.year}</span>
                </div>
              </div>

              {/* Features */}
              {vehicle.features && vehicle.features.length > 0 && (
                <>
                  <h3 className="font-bold uppercase tracking-wider text-sm mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Dotazioni
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {vehicle.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-1.5 bg-muted/50 px-3 py-1.5 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        {feature}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Accepted Deposits */}
              {company && company.accepted_deposits && company.accepted_deposits.length > 0 && (
                <>
                  <h3 className="font-bold uppercase tracking-wider text-sm mb-4 mt-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Caparra Accettata
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {company.accepted_deposits.map((deposit, index) => {
                      const labels = {
                        carta_credito: "Carta di Credito",
                        bancomat: "Bancomat",
                        assegno: "Assegno",
                        contanti: "Contanti"
                      };
                      return (
                        <div key={index} className="flex items-center gap-1.5 bg-primary/10 px-3 py-1.5 text-sm border border-primary/20">
                          <CreditCard className="h-4 w-4 text-primary" />
                          {labels[deposit] || deposit}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Right Column - Booking Card */}
          <div className="lg:col-span-1">
            <div className="bg-background border border-border p-6 sticky top-24">
              <h3 className="font-bold uppercase tracking-wider text-sm mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Tariffe
              </h3>

              {/* Show pricing tiers if available */}
              {pricing && pricing.tiers && pricing.tiers.length > 0 ? (
                <div className="space-y-2 mb-6">
                  {pricing.tiers.map((tier, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-border">
                      <span className="text-muted-foreground">
                        {tier.km_tier === "illimitati" ? "Km Illimitati" : `${tier.km_tier} km/giorno`}
                      </span>
                      <span className="font-mono font-bold">{formatCurrency(tier.price)}/giorno</span>
                    </div>
                  ))}
                  {pricing.extra_km_cost && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Costo extra: {formatCurrency(pricing.extra_km_cost)}/km oltre la soglia
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">Tariffa giornaliera</span>
                    <span className="font-mono font-bold text-lg text-primary">{formatCurrency(vehicle.daily_rate)}/giorno</span>
                  </div>
                </div>
              )}

              <Button
                className="w-full btn-primary"
                onClick={() => setShowBookingDialog(true)}
                data-testid="book-vehicle-btn"
              >
                Prenota Ora
              </Button>

              {!isAuthenticated && (
                <p className="text-xs text-muted-foreground text-center mt-3">
                  Devi accedere per prenotare
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Booking Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              Prenota {vehicle.brand} {vehicle.model}
            </DialogTitle>
            <DialogDescription>
              Seleziona le date e completa la prenotazione
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Date Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="input-label">Data Ritiro</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start" data-testid="start-date-picker">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, "dd/MM/yyyy", { locale: it }) : "Seleziona"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      disabled={(date) => date < new Date()}
                      locale={it}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="input-label">Ora Ritiro</label>
                <Select value={pickupTime} onValueChange={setPickupTime}>
                  <SelectTrigger className="w-full" data-testid="pickup-time-select">
                    <Clock className="mr-2 h-4 w-4" />
                    <SelectValue placeholder="Seleziona ora" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeOptions.map((time) => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="input-label">Data Consegna</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start" data-testid="end-date-picker">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, "dd/MM/yyyy", { locale: it }) : "Seleziona"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      disabled={(date) => date < (startDate || new Date())}
                      locale={it}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="input-label">Ora Consegna</label>
                <Select value={returnTime} onValueChange={setReturnTime}>
                  <SelectTrigger className="w-full" data-testid="return-time-select">
                    <Clock className="mr-2 h-4 w-4" />
                    <SelectValue placeholder="Seleziona ora" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeOptions.map((time) => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* KM Tier Selection */}
            <div>
              <label className="input-label flex items-center gap-2">
                <Gauge className="h-4 w-4" />
                Tariffa Chilometrica
              </label>
              <Select value={kmTier} onValueChange={setKmTier}>
                <SelectTrigger className="w-full" data-testid="km-tier-select">
                  <SelectValue placeholder="Seleziona scaglione km" />
                </SelectTrigger>
                <SelectContent>
                  {KM_TIERS.map((tier) => {
                    const price = getPriceForTier(tier.id);
                    return (
                      <SelectItem key={tier.id} value={tier.id}>
                        {tier.label} {price ? `- ${formatCurrency(price)}/giorno` : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {pricing?.extra_km_cost && (
                <p className="text-xs text-muted-foreground mt-1">
                  Costo extra: {formatCurrency(pricing.extra_km_cost)}/km oltre la soglia
                </p>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="input-label">Note (opzionale)</label>
              <Textarea
                placeholder="Aggiungi eventuali note per la prenotazione..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="booking-notes"
              />
            </div>

            {/* Price Summary */}
            {startDate && endDate && (
              <div className="bg-muted/50 p-4 border border-border">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-muted-foreground">Durata</span>
                  <span className="font-mono">{differenceInDays(endDate, startDate) || 1} giorni</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-muted-foreground">Tariffa</span>
                  <span className="font-mono">{KM_TIERS.find(t => t.id === kmTier)?.label}</span>
                </div>
                <div className="flex justify-between items-center text-lg font-bold">
                  <span>Totale Stimato</span>
                  <span className="text-primary font-mono">{formatCurrency(calculatePrice())}</span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookingDialog(false)}>
              Annulla
            </Button>
            <Button
              className="btn-primary py-2"
              onClick={handleBooking}
              disabled={bookingLoading || !startDate || !endDate || !pickupTime || !returnTime}
              data-testid="confirm-booking-btn"
            >
              {bookingLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Invio...
                </>
              ) : (
                "Conferma Prenotazione"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
